import type { InputHTMLAttributes, ReactNode } from 'react';

import { Field } from '../Field/Field';
import styles from './TextField.module.css';

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className'> {
  label: string;
  /** Метка видна не всегда: в панели инструментов она дублирует контекст. */
  hideLabel?: boolean | undefined;
  /** Иконка внутри поля слева. Декоративная, скрыта от скринридеров. */
  icon?: ReactNode | undefined;
  hint?: string | undefined;
  /** Текст ошибки. Присутствие переводит поле в невалидное состояние. */
  error?: string | undefined;
  className?: string | undefined;
}

/**
 * Поле ввода DESIGN_SYSTEM §5.7: метка сверху обычным регистром, контрол
 * `--control-height-md`, подсказка и ошибка снизу. Ошибка — бордовая рамка
 * плюс текст с иконкой (рисует `Field`), никогда только цвет рамки.
 */
export function TextField({
  label,
  hideLabel,
  icon,
  hint,
  error,
  className,
  id,
  ...rest
}: TextFieldProps) {
  return (
    <Field
      label={label}
      hideLabel={hideLabel}
      hint={hint}
      error={error}
      id={id}
      className={className}
    >
      {({ fieldId, describedBy, invalid }) => (
        <div className={[styles.control, invalid && styles.controlError].filter(Boolean).join(' ')}>
          {icon ? (
            <span className={styles.icon} aria-hidden="true">
              {icon}
            </span>
          ) : null}
          <input
            id={fieldId}
            className={styles.input}
            aria-invalid={invalid ? true : undefined}
            aria-describedby={describedBy}
            {...rest}
          />
        </div>
      )}
    </Field>
  );
}
